import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Shield } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [track, setTrack] = useState("initial");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await register(name, email, password, track, phone);
      nav("/dashboard");
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-12">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-sm">
        <div className="px-8 pt-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-8">
            <div className="h-9 w-9 bg-slate-900 flex items-center justify-center rounded-sm">
              <Shield className="h-5 w-5 text-white" />
            </div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">CA CCW Training</div>
          </Link>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>
            Create account
          </h1>
          <p className="text-sm text-slate-600 mt-2">Enroll in the 8-hour California CCW curriculum.</p>
        </div>
        <form className="px-8 pb-8 pt-6 space-y-5" onSubmit={submit}>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Full legal name</label>
            <input
              data-testid="register-name"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border border-slate-300 rounded-sm px-3 py-2.5 focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
            />
            <p className="text-xs text-slate-500 mt-1">Used on your completion acknowledgement letter.</p>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Email</label>
            <input
              data-testid="register-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border border-slate-300 rounded-sm px-3 py-2.5 focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Password</label>
            <input
              data-testid="register-password"
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border border-slate-300 rounded-sm px-3 py-2.5 focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
            />
            <p className="text-xs text-slate-500 mt-1">Minimum 8 characters.</p>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Mobile phone (optional)</label>
            <input
              data-testid="register-phone"
              type="tel"
              placeholder="+1 415 555 0123"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full border border-slate-300 rounded-sm px-3 py-2.5 focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
            />
            <p className="text-xs text-slate-500 mt-1">Used for SMS reminders for your range qualification day.</p>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Course track</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <label data-testid="track-initial" className={`flex flex-col px-4 py-3 border rounded-sm cursor-pointer ${track === "initial" ? "border-slate-900 bg-slate-50" : "border-slate-200"}`}>
                <span className="flex items-center gap-2">
                  <input type="radio" name="track" checked={track === "initial"} onChange={() => setTrack("initial")} />
                  <span className="font-semibold text-slate-900 text-sm">Initial — 8 hours</span>
                </span>
                <span className="text-xs text-slate-500 mt-1 pl-6">First-time CCW applicants.</span>
              </label>
              <label data-testid="track-renewal" className={`flex flex-col px-4 py-3 border rounded-sm cursor-pointer ${track === "renewal" ? "border-slate-900 bg-slate-50" : "border-slate-200"}`}>
                <span className="flex items-center gap-2">
                  <input type="radio" name="track" checked={track === "renewal"} onChange={() => setTrack("renewal")} />
                  <span className="font-semibold text-slate-900 text-sm">Renewal — 4 hours</span>
                </span>
                <span className="text-xs text-slate-500 mt-1 pl-6">Existing CCW holders. Same 6 modules, halved minimums.</span>
              </label>
            </div>
          </div>
          {err && (
            <div data-testid="register-error" className="bg-red-50 border border-red-200 text-red-800 text-sm px-3 py-2 rounded-sm">
              {err}
            </div>
          )}
          <button
            data-testid="register-submit"
            disabled={busy}
            className="w-full bg-slate-900 hover:bg-slate-800 text-white py-3 rounded-sm font-medium transition-colors disabled:opacity-50"
          >
            {busy ? "Creating account…" : "Create Account"}
          </button>
          <div className="text-sm text-center text-slate-600">
            Already enrolled?{" "}
            <Link to="/login" data-testid="link-to-login" className="text-blue-700 hover:underline font-medium">
              Sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
